import { useState, type JSX } from 'react'

import { instantFromShopClock, shopClock } from '@lensmira/domain'
import { formatDayTime } from '@lensmira/i18n'

import { Ltr, useTranslation } from '../i18n.js'
import { DataSurface } from './DataSurface.js'
import { FrameChoice, frameName, type FrameOption } from './FrameChoice.js'

/**
 * Holding a frame for the customer in front of you. §6.33 `RES-001`, `RES-002`; `decisions/0032`.
 *
 * **On the customer record and not on the stock screen**, because a hold is a promise to a named
 * person and the shop floor's own question is "how many can I sell", which the position's
 * availability already answers. The dispenser is standing with the customer when the promise is
 * made, so this is where the frame picker belongs.
 *
 * **The picker is `FrameChoice`, the same one the order form uses.** A second frame list beside it
 * is how two screens end up naming one frame two ways, which `frameName` exists to prevent.
 *
 * **Ended holds stay on the list.** The question somebody asks at the counter is "what happened to
 * the frame I was promised", and an empty list where a hold expired last night answers it wrongly.
 * `RES-002` notifies the dispenser, and this is where the customer's half of that conversation is.
 */

export interface HoldView {
  readonly id: string
  readonly stockItemId: string
  readonly quantity: number
  /** ISO instant. */
  readonly expiresAt: string
  readonly heldByStaffId: string
  readonly expiredAt?: string
  readonly releasedAt?: string
  readonly releaseReason?: string
  readonly version: number
  /** The frame's name, where the read could resolve it. Absent is said in words, not left blank. */
  readonly label?: string
  /** The branch's own zone, which is what turns the instant above into a clock somebody reads. */
  readonly timezone: string
}

export interface StockHoldsProps {
  readonly holds: readonly HoldView[] | null
  /**
   * Set when the holds could not be read. Kept apart from `holds` because an empty list and a failed
   * read are different statements: a failed read shown as "no frame is on hold" tells a dispenser
   * there is nothing to honour when there may be.
   */
  readonly loadError?: string
  readonly onRetry?: () => void
  /** The frame picker's source, already scoped to this branch by the caller. */
  readonly loadFrames: (query: string) => Promise<readonly FrameOption[]>
  readonly onHold: (input: {
    readonly stockItemId: string
    readonly quantity: number
    readonly expiresAt: string
  }) => void
  readonly onRelease: (input: {
    readonly reservationId: string
    readonly reason: string
    readonly expectedVersion: number
  }) => void
  /** `stock_view` and `dispense_create`, both. Without them the panel reads and offers nothing. */
  readonly canHold: boolean
  /**
   * The branch's zone. Every instant on this panel is read and written against it: a
   * `datetime-local` input has no zone of its own, so a value produced from `toISOString()` is UTC
   * presented as local, which in Karachi is five hours out in both directions with nothing on
   * screen saying so. `shopClock` and `instantFromShopClock` are the pair.
   */
  readonly timezone: string
  readonly nowIso: string
  /**
   * Bumped by the caller once a write has settled, which remounts the frame picker so it re-reads.
   *
   * A hold placed or released changes what is available, and the picker's list was read before it.
   * Without this, releasing a hold left the chooser still saying "9 to sell, one held" beside a
   * row that says the hold is released: two numbers on one screen disagreeing, with the stale one
   * being the one a dispenser would act on.
   */
  readonly shelfVersion?: number
}

export function StockHolds({
  holds,
  loadError,
  onRetry,
  loadFrames,
  onHold,
  onRelease,
  canHold,
  timezone,
  nowIso,
  shelfVersion = 0,
}: StockHoldsProps): JSX.Element {
  const { t, locale } = useTranslation()
  const [chosen, setChosen] = useState<FrameOption | null>(null)
  const [quantity, setQuantity] = useState('1')
  /**
   * Three days from now, as a starting point and not a rule. `RES-001` requires an expiry and does
   * not say what it should be, and a shop that always holds for a week and one that holds until
   * Saturday are both reasonable. A prefilled field is a suggestion; a default applied silently is
   * a policy nobody chose.
   */
  const [expiresAt, setExpiresAt] = useState(
    shopClock(Date.parse(nowIso) + 3 * 86_400_000, timezone),
  )
  const [releasing, setReleasing] = useState<string | null>(null)
  const [reason, setReason] = useState('')

  const live = (hold: HoldView): boolean =>
    hold.expiredAt === undefined && hold.releasedAt === undefined

  const state = (hold: HoldView): string => {
    if (hold.releasedAt !== undefined) {
      return hold.releaseReason === undefined
        ? t('holds.state.released')
        : t('holds.state.releasedFor', { reason: hold.releaseReason })
    }
    if (hold.expiredAt !== undefined) return t('holds.state.expired')
    return t('holds.state.held')
  }

  return (
    <section className="lm-holds" aria-labelledby="holds-heading">
      <h2 id="holds-heading" className="lm-screen__subheading">
        {t('holds.heading')}
      </h2>

      {/*
        Ended holds stay on the list, so empty means this customer was never promised a frame. That
        is why the first-run copy can invite, but only for a role that can place one: an accountant
        told to "pick one below" would be pointed at a form they do not have.
      */}
      <DataSurface
        state={
          loadError !== undefined
            ? { kind: 'error', message: loadError, ...(onRetry === undefined ? {} : { onRetry }) }
            : holds === null
              ? { kind: 'loading' }
              : holds.length === 0
                ? { kind: 'empty' }
                : { kind: 'ready' }
        }
        shape="rows"
        rows={2}
        what={t('holds.what')}
        emptyMessage={canHold ? t('holds.emptyCanHold') : t('holds.none')}
      >
        <ul className="lm-holds__list">
          {(holds ?? []).map((hold) => (
            <li key={hold.id} className="lm-hold">
              <span className="lm-hold__what">{hold.label ?? t('holds.unknownFrame')}</span>
              <span className="lm-hold__quantity" dir="ltr">
                {t('holds.quantity', { count: hold.quantity })}
              </span>
              <span className="lm-hold__state">{state(hold)}</span>
              <span className="lm-hold__until">
                {/* Appendix J: an instant renders in the branch's zone, never the browser's. */}
                {t('holds.until')} <Ltr>{formatDayTime(hold.expiresAt, hold.timezone, locale)}</Ltr>
              </span>

              {canHold && live(hold) ? (
                releasing === hold.id ? (
                  <form
                    className="lm-hold__release"
                    onSubmit={(e) => {
                      e.preventDefault()
                      onRelease({
                        reservationId: hold.id,
                        reason,
                        expectedVersion: hold.version,
                      })
                      setReleasing(null)
                      setReason('')
                    }}
                  >
                    <label className="lm-field">
                      <span className="lm-field__label">{t('holds.reason')}</span>
                      <input
                        className="lm-input"
                        value={reason}
                        onChange={(e) => setReason(e.currentTarget.value)}
                      />
                    </label>
                    <button type="submit" className="lm-button lm-button--primary">
                      {t('holds.releaseConfirm')}
                    </button>
                    <button
                      type="button"
                      className="lm-button"
                      onClick={() => {
                        setReleasing(null)
                        setReason('')
                      }}
                    >
                      {t('holds.cancel')}
                    </button>
                  </form>
                ) : (
                  <button
                    type="button"
                    className="lm-button lm-button--secondary"
                    onClick={() => {
                      setReleasing(hold.id)
                    }}
                  >
                    {t('holds.release')}
                  </button>
                )
              ) : null}
            </li>
          ))}
        </ul>
      </DataSurface>

      {canHold ? (
        <form
          className="lm-holds__new"
          onSubmit={(e) => {
            e.preventDefault()
            if (chosen === null) return
            onHold({
              stockItemId: chosen.stockItemId,
              quantity: Number(quantity),
              // Back to an instant. The control holds the shop's wall clock, and the inverse of
              // what filled it is what turns it into the moment the server stores.
              expiresAt: instantFromShopClock(expiresAt, timezone),
            })
            setChosen(null)
            setQuantity('1')
          }}
        >
          <h3 className="lm-screen__subheading">{t('holds.newHeading')}</h3>

          <FrameChoice
            key={shelfVersion}
            load={loadFrames}
            {...(chosen === null ? {} : { chosenStockItemId: chosen.stockItemId })}
            onChoose={(option) => {
              setChosen(option)
            }}
          />

          {chosen === null ? (
            <p className="lm-hint">{t('holds.pickAFrame')}</p>
          ) : (
            <p className="lm-hint">{frameName(chosen)}</p>
          )}

          <label className="lm-field">
            <span className="lm-field__label">{t('holds.quantityLabel')}</span>
            <input
              className="lm-input"
              type="number"
              min="1"
              dir="ltr"
              value={quantity}
              onChange={(e) => setQuantity(e.currentTarget.value)}
            />
          </label>

          <label className="lm-field">
            {/* `RES-001`: a hold has an end, and the shopkeeper chooses it. */}
            <span className="lm-field__label">{t('holds.untilLabel')}</span>
            <input
              className="lm-input"
              type="datetime-local"
              dir="ltr"
              value={expiresAt}
              onChange={(e) => setExpiresAt(e.currentTarget.value)}
            />
          </label>

          <button type="submit" className="lm-button lm-button--primary" disabled={chosen === null}>
            {t('holds.hold')}
          </button>
        </form>
      ) : null}
    </section>
  )
}
